import { useDirectMarkerUpdates } from 'components/minute/builders/v2/useDirectMarkerUpdates';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { CheckValue } from 'elements/minute/sidebar/sidebarComponents/atomic/CheckValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { SidebarSectionRow } from 'elements/minute/sidebar/sidebarSection/SidebarSectionRow';
import { useDetailCRUD } from 'hooks/detail/useDetailCRUD';
import {
    mapIconPreviewIndoorStandLogoToInitialDate,
    useIconPreview
} from 'hooks/marker/useIconPreview';
import { useUploadImage } from 'libs/hooks/io/useUploadImage';
import { EUploadResolution } from 'libs/utils/logic/operations/fileOperations';
import { useContext, useEffect, useState } from 'react';
import { IIndoorStandWorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    marker: IIndoorStandWorldMarker;
}

export const IndoorStandMarkerLogoSection = (props: Props) => {
    const ctxEdit = useContext(CtxEdit);
    const { uploaded: uploadedImage, setFiles } = useUploadImage(
        [EUploadResolution.SMALL, EUploadResolution.BIG],
        ctxEdit.map?.worldMapId
    );
    const [uploadingImage, setUploadingImage] = useState<boolean>(false);
    const directMarkerUpdates = useDirectMarkerUpdates();
    const detailCRUD = useDetailCRUD(ctxEdit.map.worldMapId);

    const [storyHeaderImage, setStoryHeaderImage] = useState(null);

    const iconPreview = useIconPreview({
        mapRef: ctxEdit.mapRef,
        worldMarkers: ctxEdit.worldMarkers,
        markerIds: [props.marker.id],
        initialData: mapIconPreviewIndoorStandLogoToInitialDate(props.marker.elementData),
        iconPrefix: 'temp-icon|',
        markerType: 'icon',
        onNewImageGenerated: ({ isInitialLoad }) => {
            if (isInitialLoad) {
                return;
            }
            const newMarker: IIndoorStandWorldMarker = {
                ...props.marker,
                elementData: {
                    ...props.marker.elementData,
                    imageSrc: `temp-icon|-${props.marker.id}`
                }
            };
            directMarkerUpdates.updateIconLikeMarker('indoor_stand', newMarker);

            ctxEdit.updateWorldMarkerData(props.marker.id, {
                imageSrc: `temp-icon|-${props.marker.id}`
            });
        }
    });

    function onImageChanged(url: string) {
        iconPreview.setImage(url);
        ctxEdit.updateWorldMarkerData(props.marker.id, {
            logoGeneratedImageUrl: url
        });
    }

    useEffect(() => {
        if (uploadedImage?.data?.imageUrls) {
            const url = uploadedImage?.data?.imageUrls?.[EUploadResolution.SMALL];

            onImageChanged(url);

            setUploadingImage(false);
        }
    }, [uploadedImage]);

    useEffect(() => {
        if (props.marker?.storyIds?.length !== 1) {
            return;
        }
        const readStory = async () => {
            const story = await detailCRUD.readDetail(props.marker.storyIds[0]);
            if (story.data?.moment?.header?.imageUrl) {
                setStoryHeaderImage(story.data.moment.header.imageUrl);
            }
        };
        readStory();
    }, [props.marker.storyIds?.length]);

    return (
        <SidebarSection title={Localization.STAND_MARKER1} icon={{ iconName: 'image' }} topBorder>
            <SidebarContent withTopPadding>
                <S_SidebarSectionRowHorizontal>
                    <label>
                        <S_InputFile
                            type="file"
                            accept="image/x-png,image/png,image/gif,image/jpeg,image/jpg,image/heic,image/heif,image/svg+xml,.heic;capture=camera"
                            onChange={(e) => {
                                setUploadingImage(true);
                                setFiles(Array.from(e.target['files']));
                            }}
                        />

                        <ActionButton
                            text={
                                uploadingImage
                                    ? Localization.UPLOADING + '...'
                                    : Localization.UPLOAD_ACTION
                            }
                            icon={{ iconName: 'upload' }}
                            disabled={Boolean(uploadingImage)}
                        />
                    </label>
                    <ActionButton
                        text={Localization.LOAD_FROM_DETAIL}
                        icon={{ iconName: 'bullseye-pointer' }}
                        disabled={Boolean(uploadingImage) || !storyHeaderImage}
                        onClick={() => {
                            onImageChanged(storyHeaderImage);
                        }}
                    />
                </S_SidebarSectionRowHorizontal>

                {props.marker.elementData.logoGeneratedImageUrl && (
                    <SidebarSectionRow alignment="end">
                        <ActionButton
                            icon={{ iconName: 'trash-can' }}
                            onClick={() => {
                                onImageChanged(null);
                            }}
                        />
                    </SidebarSectionRow>
                )}

                {props.marker.elementData.logoGeneratedImageUrl && (
                    <SidebarSectionRow title={Localization.DISPLAY_IN_RECTANGLE}>
                        <CheckValue
                            label={Localization.ENABLED}
                            value={props.marker.elementData.logoGeneratedStyleId === 'white-box'}
                            onChange={(value) => {
                                const updateValue = value ? 'white-box' : 'default';
                                iconPreview.setStyleId(updateValue);
                                ctxEdit.updateWorldMarkerData(props.marker.id, {
                                    logoGeneratedStyleId: updateValue
                                });
                            }}
                        />
                    </SidebarSectionRow>
                )}
            </SidebarContent>
            <S_Canvas ref={iconPreview.canvasRef} width={200} height={90} />
        </SidebarSection>
    );
};

const S_InputFile = styled.input`
    display: none;
`;

const S_SidebarSectionRowHorizontal = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    margin-bottom: 10px;
`;

const S_Canvas = styled.canvas`
    width: 200px;
    height: 90px;
    display: none;
`;
